import { validateChildOutput } from '../features/multiPackagePrint/outputManifest';
import downloadCsv from "./downloadCsv";
import {showToastError, startProgressToast, type ProgressToast} from "../components/ToastNotifications";
import { getPrintRunById } from "../hooks/usePrintRuns";
import { getPackageById } from '../hooks/usePackages';
import { printRunRecordCsvFilename, printRunRecordPackageFilenameBase, printRunRecordPackageLabel } from './printRunFilename';
import jsonToCsv from "./jsonToCsv";
import type { PrintRunArtifact, PrintRunArtifactRequest } from "./downloadPrintRunOutput";

let cachedPrintRun: { key: string; data: any } | null = null;

const fetchPrintRunCached = async (job, resolvePackageLabel = true) => {
  const key = `${job.organizationId}:${job.id}`;
  let data = cachedPrintRun && cachedPrintRun.key === key
    ? cachedPrintRun.data
    : await getPrintRunById(job.organizationId, job.id);
  if (resolvePackageLabel && job.packageCoaId == null && data?.sourceName !== 'Multi-Package Print'
    && !printRunRecordPackageLabel(data, job) && data?.packageId) {
    try {
      const packageData = await getPackageById(job.organizationId, data.packageId);
      data = { ...data, packageLabel: packageData?.label };
    } catch (error) {
      console.warn('[exportPrintRunJob] Could not resolve package UID for CSV filename:', error);
    }
  }
  cachedPrintRun = { key, data };
  return data;
};

const CSV_HEADERS = ["Unit Code", "index", "reel", "kind"];

export const createPrintRunCsvArtifact = async (
  job: PrintRunArtifactRequest & { organizationId: number },
  resolvePackageLabel = true,
): Promise<PrintRunArtifact> => {
  const jobById = job.expectedChildOutput || job.packageCoaId != null
    ? await getPrintRunById(job.organizationId, job.id)
    : await fetchPrintRunCached(job, resolvePackageLabel);
  validateChildOutput(job.organizationId, job, jobById);
  if (job.packageCoaId != null && Number(jobById?.packageCoaId) !== Number(job.packageCoaId)) {
    throw new Error('Print run COA does not match the completed child output manifest');
  }
  const expectsChildManifest = job.packageCoaId != null || jobById?.sourceName === 'Multi-Package Print';
  if (expectsChildManifest && job.packageLabel && jobById?.packageLabel !== job.packageLabel) {
    throw new Error('Print run package does not match the completed child output manifest');
  }
  const range = jobById?.range || jobById?.Range || [];
  if (jobById?.sourceName === 'Multi-Package Print'
    && (!Array.isArray(jobById.qrs) || jobById.qrs.length !== jobById.count
      || range.length !== 1 || !Array.isArray(range[0])
      || range[0][1] - range[0][0] + 1 !== jobById.count)) {
    throw new Error('Original child QR range is unavailable');
  }
  const start = (range.length > 0 && Array.isArray(range[0])) ? range[0][0] : 0;
  const records = (jobById?.qrs || []).map((qr, idx) => ({
    "Unit Code": qr, index: start + idx, reel: 0, kind: 'each',
  }));
  const filename = printRunRecordCsvFilename(jobById, job);
  return {
    blob: new Blob(["\uFEFF" + jsonToCsv(records, CSV_HEADERS)], { type: "text/csv;charset=utf-8;" }),
    fileName: filename,
  };
};

export const clearPrintRunCache = () => {
  cachedPrintRun = null;
};

const exportPrintRunJob = async (job, existingToast?: ProgressToast, preferredFilename?: string) => {
  const jobLabel = job?.jobName || 'Print Run';
  const processingLabel = `Preparing CSV export for ${jobLabel}...`;
  const progress = existingToast ?? startProgressToast(processingLabel);
  const stopAnimating = progress.animateTo(0.9, processingLabel);
  try {
    const displayedFilename = preferredFilename?.trim();
    const artifact = await createPrintRunCsvArtifact(job, !displayedFilename);
    const jobById = await fetchPrintRunCached(job, !displayedFilename);
    const range = jobById?.range || jobById?.Range || [];
    const start = (range.length > 0 && Array.isArray(range[0])) ? range[0][0] : 0;
    const records = (jobById?.qrs || []).map((qr, idx) => ({
      "Unit Code": qr, index: start + idx, reel: 0, kind: 'each',
    }));
    // Callers that show a filename (the print dialog card) pass it so the download always matches.
    downloadCsv(records, displayedFilename || artifact.fileName, CSV_HEADERS);
    progress.succeed(`CSV ready for ${jobById.jobName || jobLabel}`);
  }
  catch (e: any) {
    console.error(e);
    const message = `The Print Run could not be exported: ${e?.message ?? 'please try again.'}`;
    progress.fail(message);
  } finally {
    stopAnimating();
  }
}

export const exportPrintRunReel = async (job, reelNum: number, rangeStart: number, rangeEnd: number, preferredBaseName?: string) => {
  try {
    const displayedBaseName = preferredBaseName?.trim();
    const jobById = await fetchPrintRunCached(job, !displayedBaseName);
    const range = jobById?.range || jobById?.Range || [];
    const globalStart = (range.length > 0 && Array.isArray(range[0])) ? range[0][0] : 0;

    const qrs = jobById?.qrs || [];
    const sliceStart = rangeStart - 1;
    const sliceEnd = rangeEnd;
    const reelQrs = qrs.slice(sliceStart, sliceEnd);

    const records = reelQrs.map((qr, idx) => ({
      "Unit Code": qr,
      index: globalStart + sliceStart + idx,
      reel: reelNum,
      kind: 'each'
    }));

    const slug = displayedBaseName
      || printRunRecordPackageFilenameBase(jobById, job);
    downloadCsv(records, `${slug}-reel-${reelNum}.csv`, ["Unit Code", "index", "reel", "kind"]);
  } catch (e) {
    console.error(e);
    const message = e instanceof Error
      ? e.message
      : 'The print job reel could not be exported. Please try again.';
    showToastError(message);
  }
};

export default exportPrintRunJob;
